/**
 * Mobile Performance & UX Enhancements
 * ChasingVance Theme
 */

(function() {
  'use strict';

  // Check if device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  if (!isMobile && !isTouch) return;

  // ==========================================
  // Lazy Load Images
  // ==========================================
  function initLazyLoading() {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
          img.classList.add('loaded');
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01
    });

    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      imageObserver.observe(img);
    });
  }

  // ==========================================
  // Smooth Scroll Enhancement
  // ==========================================
  function initSmoothScroll() {
    // Only if user doesn't prefer reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#' || href === '#!') return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // ==========================================
  // Prevent Double Tap Zoom on Buttons
  // ==========================================
  function preventDoubleTapZoom() {
    let lastTouchEnd = 0;

    document.addEventListener('touchend', function(event) {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        // Check if target is interactive element
        const target = event.target;
        if (target.matches('button, .button, a, input, select, textarea')) {
          event.preventDefault();
        }
      }
      lastTouchEnd = now;
    }, { passive: false });
  }

  // ==========================================
  // Enhanced Touch Feedback
  // ==========================================
  function initTouchFeedback() {
    const interactiveElements = document.querySelectorAll('button, .button, a[href], .card__link');

    interactiveElements.forEach(element => {
      element.addEventListener('touchstart', function() {
        this.classList.add('touch-active');
      }, { passive: true });

      element.addEventListener('touchend', function() {
        this.classList.remove('touch-active');
      }, { passive: true });

      element.addEventListener('touchcancel', function() {
        this.classList.remove('touch-active');
      }, { passive: true });
    });
  }

  // ==========================================
  // Optimize Viewport Height for Mobile Browsers
  // ==========================================
  function setMobileVH() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  // ==========================================
  // Debounced Resize Handler
  // ==========================================
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // ==========================================
  // Passive Scroll Performance
  // ==========================================
  function optimizeScrolling() {
    let ticking = false;
    let lastScrollY = window.scrollY;

    window.addEventListener('scroll', function() {
      lastScrollY = window.scrollY;

      if (!ticking) {
        window.requestAnimationFrame(function() {
          // Add scroll-based classes or animations here
          if (lastScrollY > 100) {
            document.body.classList.add('scrolled');
          } else {
            document.body.classList.remove('scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ==========================================
  // Prevent iOS Bounce Effect on Modals
  // ==========================================
  function preventModalBounce() {
    const modals = document.querySelectorAll('.modal, .drawer, .cart-drawer, .menu-drawer');

    modals.forEach(modal => {
      let startY = 0;

      modal.addEventListener('touchstart', function(e) {
        startY = e.touches[0].pageY;
      }, { passive: true });

      modal.addEventListener('touchmove', function(e) {
        const scrollable = e.target.closest('.modal__content, .drawer__content');
        if (!scrollable) return;

        const scrollTop = scrollable.scrollTop;
        const scrollHeight = scrollable.scrollHeight;
        const clientHeight = scrollable.clientHeight;
        const deltaY = e.touches[0].pageY - startY;

        // Prevent bounce at top
        if (scrollTop === 0 && deltaY > 0) {
          e.preventDefault();
        }
        // Prevent bounce at bottom
        if (scrollTop + clientHeight >= scrollHeight && deltaY < 0) {
          e.preventDefault();
        }
      }, { passive: false });
    });
  }

  // ==========================================
  // Fast Click Enhancement
  // ==========================================
  function initFastClick() {
    // Modern browsers don't need this anymore, but add for older devices
    if ('ontouchstart' in window) {
      document.body.addEventListener('touchstart', function() {}, { passive: true });
    }
  }

  // ==========================================
  // Add Mobile-Specific Body Class
  // ==========================================
  function addMobileClass() {
    document.body.classList.add('is-mobile');
    if (isTouch) {
      document.body.classList.add('is-touch');
    }
  }

  // ==========================================
  // Connection Quality Detection
  // ==========================================
  function detectConnection() {
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

      if (connection) {
        // Save connection quality
        const effectiveType = connection.effectiveType; // '4g', '3g', '2g', 'slow-2g'
        document.body.dataset.connection = effectiveType;

        // Adjust features based on connection
        if (effectiveType === 'slow-2g' || effectiveType === '2g') {
          // Disable autoplay, reduce animations, etc.
          document.body.classList.add('slow-connection');
        }
      }
    }
  }

  // ==========================================
  // Initialize All Features
  // ==========================================
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    addMobileClass();
    initLazyLoading();
    initSmoothScroll();
    preventDoubleTapZoom();
    initTouchFeedback();
    optimizeScrolling();
    preventModalBounce();
    initFastClick();
    detectConnection();

    // Set mobile viewport height
    setMobileVH();
    window.addEventListener('resize', debounce(setMobileVH, 100));

    // Orientation change handler
    window.addEventListener('orientationchange', function() {
      setTimeout(setMobileVH, 200);
    });

    console.log('🚀 Mobile optimizations loaded');
  }

  // Start initialization
  init();

})();
